import { Node, Viewport, useOnViewportChange } from 'reactflow';
import {
  Portal,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Box,
} from '@chakra-ui/react';
import {
  Blocks,
  GanttChartSquare,
  ImageIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  MessageSquare,
  Upload,
  Video,
  ArrowRight,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useAddObjectButton } from './useAddObjectButton';
import {
  createUploadDBlock,
  linkJSON,
  messageJSON,
  olJSON,
  ulJSON,
} from '../../tiptap/contentJSONs';
import { chatMessageDBlock } from '../flowEditorBlockNodeTypes';
import AddGifButton from './AddGifButton';
import { testIds as blockNodeTestIds } from '../FlowEditorBlockNode/testIds';

export function NodeOutgoingMenu({
  node,
  isHoveredOnNode,
}: {
  node: Node;
  isHoveredOnNode: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const [isHoveredNextToNode, setIsHoveredNextToNode] = useState(false);
  const shouldShowIcons = isHoveredNextToNode || isHoveredOnNode || menu;

  useOnViewportChange({
    onStart: useCallback((_viewport: Viewport) => {
      setMenu(false);
    }, []),
  });
  const nodeId = node.id;
  const message = useAddObjectButton({
    id: nodeId,
    tiptapContent: chatMessageDBlock(messageJSON),
  });
  const uList = useAddObjectButton({
    id: nodeId,
    tiptapContent: chatMessageDBlock(ulJSON),
  });
  const oList = useAddObjectButton({
    id: nodeId,
    tiptapContent: chatMessageDBlock(olJSON),
  });
  const link = useAddObjectButton({
    id: nodeId,
    tiptapContent: chatMessageDBlock(linkJSON),
  });
  const emptyBlock = useAddObjectButton({ id: nodeId, sourceHandle: nodeId });

  const uploadFile = useAddObjectButton({
    id: nodeId,
    sourceHandle: nodeId,
    tiptapContent: createUploadDBlock(),
  });

  const nodeHeight = node.height ?? 53; // 53 px is the height of empty node
  return (
    <Box
      pos="relative"
      role="group"
      zIndex={999}
      onClick={(ev) => {
        ev.stopPropagation();
      }}
    >
      <Flex
        role="group"
        position="absolute"
        w="13%"
        right={'-40px'}
        top={(nodeHeight / 60) * -1}
        // border={'1px solid red'} // useful for visual debugging
        h={nodeHeight - nodeHeight / 20}
        onMouseEnter={() => setIsHoveredNextToNode(true)}
        onMouseLeave={() => setIsHoveredNextToNode(false)}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Menu
          onOpen={() => setMenu(true)}
          onClose={() => setMenu(false)}
          isOpen={menu}
          closeOnBlur={true}
          closeOnSelect={true}
        >
          <MenuButton
            onClick={(ev) => {
              ev.stopPropagation(); // prevent the click from bubbling up to the parent and focusing the node in the editor. Focusing opens the right sidebar, which we don't want.
              return false;
            }}
            as={IconButton}
            left={'10px'}
            color="fg.default"
            zIndex={999}
            bgColor="white"
            visibility={shouldShowIcons ? 'visible' : 'hidden'}
            _hover={{ bgColor: '#F4F8FA' }}
            isRound={true}
            size="sm"
            variant="outline"
            aria-label="Add object"
            data-testid={blockNodeTestIds.addNodeButton}
            icon={<ArrowRight size="20" />}
          />
          <Portal>
            <MenuList
              onMouseLeave={() => setMenu(false)}
              maxHeight="320px"
              overflowY="auto"
              overflowX="hidden"
            >
              <MenuGroup color="gray.400" title="TEXT AND LAYOUT">
                <MenuItem onClick={emptyBlock} icon={<Blocks size="16px" />}>
                  Empty block
                </MenuItem>
                <MenuItem onClick={message} icon={<MessageSquare size="16px" />}>
                  Message
                </MenuItem>
                <MenuItem onClick={uList} icon={<List size="16px" />}>
                  Bulleted list
                </MenuItem>
                <MenuItem onClick={oList} icon={<ListOrdered size="16px" />}>
                  Numbered list
                </MenuItem>
                <MenuItem onClick={link} icon={<LinkIcon size="16px" />}>
                  Link
                </MenuItem>
              </MenuGroup>
              <MenuGroup color="gray.400" title="MEDIA AND CONTENT">
                <MenuItem icon={<ImageIcon size="16px" />}>Image</MenuItem>
                <AddGifButton sourceNode={node} />
                <MenuItem icon={<Video size="16px" />}>Media</MenuItem>
                <MenuItem icon={<GanttChartSquare size="16px" />}>Course</MenuItem>
                <MenuItem onClick={uploadFile} icon={<Upload size="16px" />}>
                  Upload File
                </MenuItem>
              </MenuGroup>
              {/* DUKE-1369: Hiding the menu as its not used.
                      Not removing since because this will be handled in face lift */}

              {/* <MenuGroup color="gray.400" title="GROUPING">
                <MenuItem icon={<GroupIcon size="16px" />} onClick={emptyContainer}>
                  Container
                </MenuItem>
              </MenuGroup> */}
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
    </Box>
  );
}
